import React from "react";
import "./Skeleton.css";

// Reusable loading placeholders. Compose the layout pieces below so each page's skeleton mirrors the
// page it stands in for. Every root is aria-hidden except SkeletonRegion, which announces the label once.

const size = (value) => (typeof value === "number" ? `${value}px` : value);

export const Skeleton = ({ width, height, radius, circle, className = "", style }) => (
  <span
    className={`sk${circle ? " sk-circle" : ""} ${className}`}
    style={{ width: size(width), height: size(height), borderRadius: size(radius), ...style }}
    aria-hidden="true"
  />
);

export const SkeletonText = ({ lines = 3, lastWidth = "60%", gap = 8, height = 10 }) => (
  <span className="sk-text" style={{ gap }} aria-hidden="true">
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton key={index} height={height} width={index === lines - 1 && lines > 1 ? lastWidth : "100%"} />
    ))}
  </span>
);

// Wraps a skeleton layout: one polite status message for assistive tech, busy state, no visual chrome.
export const SkeletonRegion = ({ label = "Loading", className = "", children }) => (
  <div className={`sk-region ${className}`} role="status" aria-busy="true" aria-live="polite">
    <span className="sk-sr-only">{label}</span>
    <div aria-hidden="true">{children}</div>
  </div>
);

export const SkeletonPageHeader = ({ actions = 1, eyebrow = true, intro = true }) => (
  <div className="sk-page-header">
    <div className="sk-page-header-copy">
      {eyebrow && <Skeleton width={110} height={10} />}
      <Skeleton width="min(280px, 70%)" height={26} radius={8} />
      {intro && <Skeleton width="min(440px, 90%)" height={12} />}
    </div>
    {actions > 0 && <div className="sk-page-header-actions">
      {Array.from({ length: actions }, (_, index) => <Skeleton key={index} width={index === actions - 1 ? 128 : 96} height={38} radius={10} />)}
    </div>}
  </div>
);

export const SkeletonCard = ({ media = false, lines = 2, footer = false, className = "" }) => (
  <div className={`sk-card ${className}`}>
    {media && <Skeleton className="sk-card-media" height={media === true ? 140 : media} radius={10} />}
    <div className="sk-card-row">
      <Skeleton width={36} height={36} radius={10} />
      <div className="sk-grow"><Skeleton width="62%" height={12} /><Skeleton width="38%" height={10} /></div>
    </div>
    {lines > 0 && <SkeletonText lines={lines} />}
    {footer && <div className="sk-card-footer"><Skeleton width={84} height={30} radius={9} /><Skeleton width={64} height={30} radius={9} /></div>}
  </div>
);

export const SkeletonCardGrid = ({ count = 6, min = 240, ...card }) => (
  <div className="sk-card-grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))` }}>
    {Array.from({ length: count }, (_, index) => <SkeletonCard key={index} {...card} />)}
  </div>
);

export const SkeletonStatGrid = ({ count = 4, min = 180 }) => (
  <div className="sk-card-grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))` }}>
    {Array.from({ length: count }, (_, index) => (
      <div className="sk-card sk-stat" key={index}>
        <Skeleton width={34} height={34} radius={10} />
        <Skeleton width="55%" height={10} />
        <Skeleton width="40%" height={24} radius={7} />
      </div>
    ))}
  </div>
);

export const SkeletonTabs = ({ count = 3 }) => (
  <div className="sk-tabs">{Array.from({ length: count }, (_, index) => <Skeleton key={index} width={index ? 92 : 110} height={34} radius={9} />)}</div>
);

export const SkeletonToolbar = ({ filters = 2, search = true }) => (
  <div className="sk-toolbar">
    {search && <Skeleton className="sk-toolbar-search" height={38} radius={10} />}
    {Array.from({ length: filters }, (_, index) => <Skeleton key={index} width={130} height={38} radius={10} />)}
  </div>
);

export const SkeletonTable = ({ rows = 6, columns = 5, toolbar = false, avatar = true }) => (
  <div className="sk-table">
    {toolbar && <SkeletonToolbar filters={typeof toolbar === "number" ? toolbar : 2} />}
    <div className="sk-table-head" style={{ gridTemplateColumns: `1.6fr repeat(${columns - 1}, 1fr)` }}>
      {Array.from({ length: columns }, (_, index) => <Skeleton key={index} width={index ? "55%" : "40%"} height={9} />)}
    </div>
    {Array.from({ length: rows }, (_, row) => (
      <div className="sk-table-row" key={row} style={{ gridTemplateColumns: `1.6fr repeat(${columns - 1}, 1fr)` }}>
        <span className="sk-card-row">
          {avatar && <Skeleton width={30} height={30} circle />}
          <span className="sk-grow"><Skeleton width={`${70 - (row % 3) * 12}%`} height={11} /></span>
        </span>
        {Array.from({ length: columns - 1 }, (_, column) => <Skeleton key={column} width={`${80 - ((row + column) % 4) * 14}%`} height={10} />)}
      </div>
    ))}
  </div>
);

export const SkeletonList = ({ rows = 5, avatar = true, meta = true, action = false }) => (
  <div className="sk-list">
    {Array.from({ length: rows }, (_, index) => (
      <div className="sk-list-row" key={index}>
        {avatar && <Skeleton width={38} height={38} radius={11} />}
        <div className="sk-grow">
          <Skeleton width={`${74 - (index % 3) * 14}%`} height={12} />
          <Skeleton width={`${48 - (index % 2) * 12}%`} height={10} />
        </div>
        {meta && <Skeleton width={54} height={10} />}
        {action && <Skeleton width={72} height={30} radius={9} />}
      </div>
    ))}
  </div>
);

export const SkeletonForm = ({ fields = 6, columns = 2, title = true, actions = true, textarea = false }) => (
  <div className="sk-form">
    {title && <div className="sk-form-title"><Skeleton width={180} height={16} /><Skeleton width="min(320px, 80%)" height={10} /></div>}
    <div className="sk-form-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {Array.from({ length: fields }, (_, index) => (
        <div className="sk-field" key={index}><Skeleton width={`${30 + (index % 3) * 12}%`} height={10} /><Skeleton height={40} radius={10} /></div>
      ))}
    </div>
    {textarea && <div className="sk-field"><Skeleton width="24%" height={10} /><Skeleton height={96} radius={10} /></div>}
    {actions && <div className="sk-form-actions"><Skeleton width={92} height={38} radius={10} /><Skeleton width={128} height={38} radius={10} /></div>}
  </div>
);

export const SkeletonToggleList = ({ rows = 5 }) => (
  <div className="sk-list">
    {Array.from({ length: rows }, (_, index) => (
      <div className="sk-list-row" key={index}>
        <div className="sk-grow"><Skeleton width={`${46 - (index % 3) * 8}%`} height={12} /><Skeleton width={`${70 - (index % 2) * 18}%`} height={10} /></div>
        <Skeleton width={40} height={22} radius={999} />
      </div>
    ))}
  </div>
);

export const SkeletonChat = ({ messages = 5 }) => (
  <div className="sk-chat">
    {Array.from({ length: messages }, (_, index) => (
      <div className={`sk-chat-row${index % 2 ? " is-out" : ""}`} key={index}>
        {index % 2 === 0 && <Skeleton width={30} height={30} circle />}
        <Skeleton className="sk-chat-bubble" width={`${42 + ((index * 17) % 30)}%`} height={index % 3 === 0 ? 58 : 40} radius={14} />
      </div>
    ))}
    <Skeleton className="sk-chat-composer" height={44} radius={12} />
  </div>
);

export const SkeletonCalendar = ({ weeks = 5 }) => (
  <div className="sk-calendar">
    {Array.from({ length: 7 }, (_, index) => <Skeleton key={`h${index}`} width="50%" height={9} className="sk-calendar-head" />)}
    {Array.from({ length: weeks * 7 }, (_, index) => (
      <div className="sk-calendar-cell" key={index}>
        <Skeleton width={18} height={10} />
        {index % 4 === 1 && <Skeleton height={18} radius={6} />}
        {index % 9 === 3 && <Skeleton height={18} radius={6} width="70%" />}
      </div>
    ))}
  </div>
);

// Two-column detail page: summary card + tabbed content.
export const SkeletonDetail = ({ tabs = 3, fields = 6 }) => (
  <>
    <div className="sk-card sk-detail-summary">
      <Skeleton width={56} height={56} radius={16} />
      <div className="sk-grow"><Skeleton width="min(240px, 60%)" height={18} /><Skeleton width="min(380px, 85%)" height={10} /></div>
      <Skeleton width={74} height={26} radius={999} />
    </div>
    <SkeletonTabs count={tabs} />
    <div className="sk-card"><SkeletonForm fields={fields} title={false} /></div>
  </>
);

export const SkeletonPanel = ({ title = true, children, className = "" }) => (
  <div className={`sk-card sk-panel ${className}`}>
    {title && <div className="sk-panel-title"><Skeleton width={34} height={34} radius={10} /><div className="sk-grow"><Skeleton width="40%" height={13} /><Skeleton width="62%" height={10} /></div></div>}
    {children}
  </div>
);

// Whole-app placeholder shown while the session is verified, before the layout mounts.
export const SkeletonShell = ({ label = "Loading your workspace" }) => (
  <div className="sk-shell">
    <span className="sk-sr-only" role="status" aria-live="polite">{label}</span>
    <aside className="sk-shell-sidebar" aria-hidden="true">
      <div className="sk-card-row"><Skeleton width={34} height={34} radius={10} /><Skeleton width={110} height={14} /></div>
      {Array.from({ length: 9 }, (_, index) => <div className="sk-card-row sk-shell-nav" key={index}><Skeleton width={18} height={18} radius={5} /><Skeleton width={`${50 + (index % 3) * 14}%`} height={11} /></div>)}
    </aside>
    <div className="sk-shell-main" aria-hidden="true">
      <div className="sk-shell-topbar"><Skeleton width={160} height={12} /><Skeleton width={120} height={28} radius={999} /></div>
      <div className="sk-shell-content">
        <SkeletonPageHeader actions={2} />
        <SkeletonStatGrid count={4} />
        <div className="sk-two-col"><SkeletonPanel><SkeletonList rows={4} /></SkeletonPanel><SkeletonPanel><SkeletonText lines={5} /></SkeletonPanel></div>
      </div>
    </div>
  </div>
);

// Generic page placeholder for loads where the destination page isn't known yet (e.g. permission checks).
export const SkeletonPage = ({ label = "Loading page" }) => (
  <SkeletonRegion label={label}>
    <SkeletonPageHeader actions={1} />
    <SkeletonStatGrid count={3} min={220} />
    <SkeletonPanel><SkeletonList rows={5} /></SkeletonPanel>
  </SkeletonRegion>
);

// Centered single-card form, used before the onboarding flow and other standalone screens mount.
export const SkeletonCenteredForm = ({ label = "Loading", fields = 6 }) => (
  <main className="sk-centered">
    <SkeletonRegion label={label}>
      <div className="sk-card sk-centered-card">
        <div className="sk-card-row"><Skeleton width={40} height={40} radius={12} /><div className="sk-grow"><Skeleton width="45%" height={16} /><Skeleton width="70%" height={10} /></div></div>
        <Skeleton height={6} radius={999} />
        <SkeletonForm fields={fields} title={false} />
      </div>
    </SkeletonRegion>
  </main>
);
